import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GroupsIcon from '@mui/icons-material/Groups';
import { jwtDecode } from 'jwt-decode';
import { API } from '../config/apiConfig';
import DashboardPageHeader from '../components/DashboardPageHeader';

const PLACEMENT_MAX_LEVEL = 18;

function placementThinLevelsFromApiCounts(levelCounts, maxLevel = PLACEMENT_MAX_LEVEL) {
  const haveByLevel = new Map();
  if (Array.isArray(levelCounts)) {
    for (const row of levelCounts) {
      if (row == null || typeof row === 'number') continue;
      const lv = Number(row.level ?? row.Level ?? 0) || 0;
      const n = Number(row.memberCount ?? row.MemberCount ?? row.count ?? 0) || 0;
      if (lv >= 1 && lv <= maxLevel) haveByLevel.set(lv, n);
    }
  }
  if (haveByLevel.size === 0) return [];
  const rows = [];
  for (let d = 1; d <= maxLevel; d += 1) {
    const need = 2 ** d;
    const have = haveByLevel.get(d) ?? 0;
    if (have < need) {
      rows.push({ level: d, have, need, gap: need - have });
    }
  }
  return rows;
}

function formatLastActivity(iso) {
  if (!iso) return 'never watched tokens';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'never watched tokens';
  return `last tokens ${d.toISOString().slice(0, 10)}`;
}

function signupLinkFromCode(code) {
  const c = String(code || '').trim();
  if (!c) return '';
  return `https://dv-apps.com/signup?ref=${encodeURIComponent(c)}`;
}

function Grow() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [thinLevels, setThinLevels] = useState([]);
  const [seats, setSeats] = useState([]);
  const [quietMembers, setQuietMembers] = useState([]);
  const [copyMsg, setCopyMsg] = useState('');

  const load = useCallback(async () => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
    if (!token) {
      setError('Please log in again.');
      setLoading(false);
      return;
    }
    let uid = null;
    try {
      const decoded = jwtDecode(token);
      uid = decoded.UserID || decoded.userId;
    } catch {
      setError('Please log in again.');
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    setError(null);
    try {
      const [countsRes, seatsRes, quietRes] = await Promise.all([
        fetch(API.downlineLevelCounts(uid, { treeType: 'PLACEMENT', maxLevel: PLACEMENT_MAX_LEVEL }), {
          headers,
        }),
        fetch(API.placementOpenSeats(uid, { maxLevel: PLACEMENT_MAX_LEVEL, limit: 20 }), { headers }),
        fetch(API.placementQuietMembers(uid, { days: 7, limit: 20 }), { headers }),
      ]);
      const countsData = await countsRes.json().catch(() => ({}));
      const seatsData = await seatsRes.json().catch(() => ({}));
      const quietData = await quietRes.json().catch(() => ({}));
      if (countsRes.ok && countsData?.success) {
        setThinLevels(placementThinLevelsFromApiCounts(countsData.levelCounts).slice(0, 8));
      } else {
        setThinLevels([]);
      }
      if (seatsRes.ok && seatsData?.success && Array.isArray(seatsData.seats)) {
        setSeats(seatsData.seats);
      } else {
        setSeats([]);
        if (!seatsRes.ok) {
          setError(seatsData?.error || 'Could not load who needs help. Deploy mlm_GetPlacementOpenSeats if needed.');
        }
      }
      if (quietRes.ok && quietData?.success && Array.isArray(quietData.members)) {
        setQuietMembers(quietData.members);
      } else {
        setQuietMembers([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to load grow helper');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const copyLink = useCallback(async (url) => {
    if (!url) return;
    try {
      if (navigator?.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        setCopyMsg('Referral link copied');
        return;
      }
    } catch {
      /* fallback */
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopyMsg('Referral link copied');
    } catch {
      setCopyMsg('Could not copy');
    }
  }, []);

  useEffect(() => {
    if (!copyMsg) return undefined;
    const t = setTimeout(() => setCopyMsg(''), 1800);
    return () => clearTimeout(t);
  }, [copyMsg]);

  const bottleneck = thinLevels[0];

  const seatRows = useMemo(
    () =>
      seats.map((seat) => ({
        ...seat,
        link: signupLinkFromCode(seat.referralCode),
      })),
    [seats]
  );

  const quietRows = useMemo(
    () =>
      quietMembers.map((row) => ({
        ...row,
        link: signupLinkFromCode(row.referralCode),
      })),
    [quietMembers]
  );

  return (
    <Box>
      <DashboardPageHeader
        title="Grow"
        subtitle="Where your placement tree is thin, and who still has an open seat"
        icon={GroupsIcon}
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {copyMsg ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCopyMsg('')}>
          {copyMsg}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Thin levels
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Each level is full at 2, 4, 8… people. First gap is the bottleneck.
              {bottleneck
                ? ` Right now that is L${bottleneck.level} (${bottleneck.have}/${bottleneck.need}).`
                : ''}
            </Typography>
            {thinLevels.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No thin levels loaded, or every counted level is full.
              </Typography>
            ) : (
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ gap: 0.75 }}>
                {thinLevels.map((row) => (
                  <Chip
                    key={`thin-L${row.level}`}
                    size="small"
                    label={`L${row.level} ${row.have}/${row.need} — need ${row.gap}`}
                    variant="outlined"
                    color={row.level === bottleneck?.level ? 'warning' : 'default'}
                  />
                ))}
              </Stack>
            )}
          </Paper>

          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Who needs help
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              People with 0 or 1 placement seat filled. Share their referral link so they can fill a seat.
            </Typography>
            {seatRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No open seats in the first 20 people.
              </Typography>
            ) : (
              <Stack spacing={1.25} divider={null}>
                {seatRows.map((seat) => (
                  <Box
                    key={seat.userId}
                    sx={{
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      {seat.name || `User ${seat.userId}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {seat.childCount}/2 seats
                      {seat.levelFromYou === 0 ? ' · you' : ` · L${seat.levelFromYou}`}
                    </Typography>
                    {seat.link ? (
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5, minWidth: 0 }}>
                        <Link
                          href={seat.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontSize: '0.875rem', wordBreak: 'break-all' }}
                        >
                          {seat.link}
                        </Link>
                        <Tooltip title="Copy referral link">
                          <IconButton size="small" onClick={() => void copyLink(seat.link)} aria-label="Copy referral link">
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No referral code
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>

          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              No tokens in 7 days
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              People in your placement tree with no ad/token watches in the last week. Nudge them, or share their link.
            </Typography>
            {quietRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nobody quiet in the first 20, or deploy mlm_GetPlacementQuietMembers.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {quietRows.map((row) => (
                  <Box
                    key={`quiet-${row.userId}`}
                    sx={{
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      {row.name || `User ${row.userId}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatLastActivity(row.lastActivityAt)}
                      {row.levelFromYou === 0 ? ' · you' : ` · L${row.levelFromYou}`}
                    </Typography>
                    {row.link ? (
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5, minWidth: 0 }}>
                        <Link
                          href={row.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontSize: '0.875rem', wordBreak: 'break-all' }}
                        >
                          {row.link}
                        </Link>
                        <Tooltip title="Copy referral link">
                          <IconButton size="small" onClick={() => void copyLink(row.link)} aria-label="Copy referral link">
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No referral code
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>
      )}
    </Box>
  );
}

export default Grow;
